import { ref, set } from 'firebase/database';
import { deleteField, writeBatch } from 'firebase/firestore';
import { ParentContext } from 'global/cnr/contexts/ParentContext';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import { _cacheGroupTypes } from 'global/cnr/contexts/useHelpers/useTimestamp';
import { logBatch } from 'global/cnr/reducers/reducerHelpers/logging';
import { createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { useCallback, useContext, useState } from 'react';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { matchUpdateTypes } from '../cnr/reducers/SportsSeasonReducer';
import { seasonalDataHelpers } from '../cnr/reducers/reducerHelpers/seasonalDataHelpers';
import { seasonalHelpers } from '../cnr/reducers/reducerHelpers/seasonalHelpers';
import { _matchResultSourceTypes } from './useMatchResults';
import { _allowSportsUpdates } from './useSeasonals';

const _sportMatchesCollectionName = '_matches_seasonal'

export const useMatchUpdate = () => {

  const appUserAccess = useAppUserAccess()
  const { uid, displayName } = appUserAccess ?? {}

  const parentContext = useContext(ParentContext);
  const { states, fns, fn_cls } = parentContext ?? {}
  const { app_state, paps_state } = states
  const { mainDb, app_ts } = app_state
  const { pathViews } = paps_state
  const { database_fns } = fns

  // sportsSeasonContext
  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const { currentSeason_info, sportPermissions, sportDataSettings } = sportsSeason_state ?? {}
  const { latestSeason, sportsKey } = currentSeason_info ?? {}
  const pks = seasonalHelpers.getPks(pathViews, latestSeason, sportsKey, ['teams'])

  const [updating, setUpdating] = useState()

  const { getSports_collectionItem, getUpdateProps } = seasonalDataHelpers(mainDb, database_fns, pathViews)

  const handle_updateMatchScore_db = useCallback(async ({
    match_edit,
    callback,
    urlOnly,
    updateRtGameDayData
  }) => {

    const _callback = () => {
      setUpdating(false)
      callback()
    }

    const _sportsYearCollectionItem = await getSports_collectionItem(pks, _sportMatchesCollectionName, true)

    if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {

      const { _itemKey: _docKey } = _sportsYearCollectionItem
      const { _matchCollectionKey, _itemKey, results, isPlayoff } = match_edit ?? {}
      let docCollectionKey = isPlayoff ? 'playoffMatches' : 'matches'
      if (urlOnly) { docCollectionKey = 'matchUrls' }
      const refPath = createRefPath_event(pathViews, [_sportMatchesCollectionName, _docKey, docCollectionKey, _matchCollectionKey])
      if (results?.setNumber) { delete results.setNumber }
      if (results?.scores) {
        _.forEach(results.scores, score => {
          delete score.complete
        })
      }
      const fieldPath = `${docCollectionKey}.${_itemKey}`
      const updates = { [`${fieldPath}.results`]: results }

      await updateMatchInDb_safe({
        mainDb,
        pathViews,
        refPath,
        fieldPath: fieldPath,
        updates: updates,
        database_fns,
        onSuccess: _callback,
        onError: (err) => console.error('Error updating match results:', err)
      })
    }
  }, []);

  const handle_updateMatchDetails_db = useCallback(async ({ matchUpdateType, match, sortedAthletes, callback }) => {
    const _callback = () => {
      setUpdating(false)
      callback()
    }
    setUpdating(true)
    const { matchData, matchData_scores } = getMatchUpdateProps({ matchUpdateType, match, sortedAthletes })
    const _sportsYearCollectionItem = await getSports_collectionItem(pks, _sportMatchesCollectionName, true)
    if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {
      const { _itemKey: _docKey } = _sportsYearCollectionItem
      const { _matchCollectionKey, _itemKey, isPlayoff } = match ?? {}
      let docCollectionKey = isPlayoff ? 'playoffMatches' : 'matches'
      const refPath = createRefPath_event(pathViews, [_sportMatchesCollectionName, _docKey, docCollectionKey, _matchCollectionKey])
      const fieldPath = `${docCollectionKey}.${_itemKey}`
      console.log('refPath', refPath)
      console.log('matchData', matchData)
      console.log('fieldPath', fieldPath)
      await updateMatchInDb_safe({
        mainDb,
        pathViews,
        refPath,
        fieldPath: fieldPath,
        updates: matchData,
        database_fns,
        onSuccess: _callback,
        onError: (err) => console.error('Error updating match results:', err)
      })
    }
    // updateMatch(fn_cls, matchPendingUpdate, match_true, match_edit, getUpdateProps)
  }, []);

  const handle_updateMatchScore_rt = useCallback(async ({ match_edit, callback }) => {

    const _callback = () => {
      setUpdating(false)
      callback()
    }

    // this will go to thumbstat
    const { results, id: matchId } = match_edit ?? {}
    const path_match = `clients/${pathViews.clients}/events/${pathViews.events}/matchDayResults/${pathViews.sports}/${matchId}/${uid}`
    const submissionRef = ref(app_ts.realtime, path_match);

    const submissionData = {
      results: results,
      displayName: displayName,
      createdAt: Date.now(),
      status: 'pending',
      resultType: _matchResultSourceTypes.matchDay
    };

    try {
      set(submissionRef, submissionData);
      _callback()
      console.log('Submission updated successfully:', submissionData);
    } catch (error) {
      _callback()
      console.error('Error updating submission:', error);
    }
  }, [])

  const handle_addToHistory = useCallback(async ({ match, currentSeason }) => {
    const _callback = () => {
      setUpdating(false)
    }
    setUpdating(true)
    const pks_history = seasonalHelpers.getPks(pathViews, null, sportsKey, ['teams'])
    const _sportsHistoryCollectionItem = await getSports_collectionItem(pks_history, '_history', true)
    if (_sportsHistoryCollectionItem && !_.isEmpty(_sportsHistoryCollectionItem)) {
      const { _itemKey } = _sportsHistoryCollectionItem
      const { _matchCollectionKey, _results } = match ?? {}
      const { winner, loser } = _results ?? {}
      const newResult = {
        runnerUp: loser.name,
        winner: winner.name
      }
      const _latestSeason = currentSeason || latestSeason
      const dataToUpdate = {
        [`history.${_matchCollectionKey}.${_latestSeason}`]: newResult
      }
      const refPath_history = createRefPath_event(pathViews, ['_history', _itemKey])
      console.log('refPath_history', refPath_history)
      console.log('dataToUpdate', dataToUpdate)

      if (_allowSportsUpdates.matches) {
        const _timestamp = Date.now()
        const batch = writeBatch(mainDb);
        database_fns.update_doc(refPath_history, dataToUpdate, null, batch)
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonInfo, pathViews.sports, _timestamp, batch)
        logBatch(batch, dataToUpdate)
        // await batch.commit()
        _callback()
      }
    }
    // updateMatch(fn_cls, matchPendingUpdate, match_true, match_edit, getUpdateProps)
  }, []);
  return {
    handle_updateMatchDetails_db,
    handle_updateMatchScore_db,
    handle_updateMatchScore_rt,
    handle_addToHistory,
    updating
  }
}

const getMatchUpdateProps = ({ match, matchUpdateType, sortedAthletes }) => {

  const { _itemKey: _matchKey, home, away, startDate, startTime, location, pending, videoUrl, isPlayoff } = match ?? {}
  const { place, poolName, matchNumber } = pending ?? {}

  const matchData = {}
  const matchData_scores = {}

  let _dataType = isPlayoff ? 'playoffMatches' : 'matches'
  let _dataType_scores = isPlayoff ? 'playoffScores' : 'scores'

  const _mk = `${_dataType}.${_matchKey}`
  const _mk_scores = `${_dataType_scores}.${_matchKey}`

  switch (matchUpdateType) {
    case matchUpdateTypes.deleteRealtimeMatch:
      // do nothing here
      break;

    case matchUpdateTypes.updateVideoUrl:
      matchData[`${_mk}.videoUrl`] = videoUrl;
      break;

    case matchUpdateTypes.deleteMatch:
      matchData[_mk] = deleteField();
      break;

    case matchUpdateTypes.removeResults:
      matchData[`${_mk}.results`] = deleteField();
      break;

    case matchUpdateTypes.postponeMatch:
      matchData[_mk + '.postponed'] = true;
      matchData[_mk + '.cancelled'] = deleteField();
      break;

    case matchUpdateTypes.cancelMatch:
      matchData[_mk + '.cancelled'] = true;
      matchData[_mk + '.postponed'] = deleteField();
      break;

    case matchUpdateTypes.resetByeMatch:
      const byeName = poolName + ' #' + matchNumber + '/' + place
      matchData[_mk + '.away'] = { id: match.seeds.away, name: byeName };
      break;

    case matchUpdateTypes.editFullMatchDetails:
      matchData[_mk + '.away'] = away;
      matchData[_mk + '.home'] = home;
      matchData[_mk + '.startDate'] = startDate;
      matchData[_mk + '.startTime'] = startTime;
      if (location) {
        matchData[_mk + '.location'] = location
      };
      break;

    case matchUpdateTypes.editMatchDetails:
      if (location) {
        matchData[_mk + '.location'] = location
      };
      matchData[_mk + '.startDate'] = startDate;
      matchData[_mk + '.startTime'] = startTime;
      matchData[_mk + '.postponed'] = deleteField();
      matchData[_mk + '.cancelled'] = deleteField();
      break;

    case matchUpdateTypes.updateMatchDayMatch:
      if (score || scores) {
        const res = {}

        if (scores) {
          if (_.isArray(scores)) {
            res.scores = _.compact(scores);
          } else {
            res.scores = scores;
          }
        }
        if (score) { res.score = score; }
        matchData[_mk + '.results'] = res;
        matchData_scores[_mk_scores] = { ...res };
      }
      break;

    case matchUpdateTypes.updateScore:

      if (score || scores) {

        const res = {}

        if (scores) {
          if (_.isArray(scores)) {
            res.scores = _.compact(scores);
          } else {
            res.scores = scores;
          }
        }
        if (score) { res.score = score; }

        let players;

        if (sortedAthletes) {
          players = {}
          sortedAthletes.forEach((sa, index) => {
            players[sa.key] = {
              firstName: sa.firstName,
              lastName: sa.lastName,
              position: index
            }
          })
          res.players = players
        }

        matchData[_mk + '.results'] = res;
        matchData_scores[_mk_scores] = { ...res };
      }
      break;

    default:
      return;
  }

  return { matchData, matchData_scores }

}

const updateMatchInDb_safe = async ({
  mainDb,
  pathViews,
  refPath,
  fieldPath,        // e.g. 'matches.abc123.results'
  updates,
  database_fns,
  onError,
  onSuccess,
  allowNew,
}) => {
  try {
    // 1. Get the document 
    const docSnap = await database_fns.get_doc({ refPath })

    if (!docSnap) {
      console.log(`Document does not exist at path: ${refPath}`)
      return;
    }

    const docData = docSnap.data ? docSnap.data() : docSnap

    // 2. Ensure FULL path exists (not just partial)
    const exists = _.has(docData, fieldPath)

    if (!exists && !allowNew) {
      console.log(`Field path does not exist: ${fieldPath}`)
      return;
    }

    console.group('safeUpdateNested')
    console.log('refPath', refPath)
    console.log('updates', updates)
    console.groupEnd()
    // 4. Perform strict update

    // refPath clients/b4C6wHP0A5AgQPTD78ue/events/pojoSports/_matches_seasonal/MVB_2026_YzZhvmotOHlY3ePS8QJj/playoffMatches/3A
    {
      // updates = playoffMatches.af361jZjajToOtYh8puq.results: 
      // {score: {
      //     "home": 3,
      //     "away": 0
      // }
      // }
    }

    if (_allowSportsUpdates.matches) {
      const _timestamp = Date.now()
      const batch = writeBatch(mainDb);
      database_fns.update_doc(refPath, updates, null, batch)
      database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonResultsInfo, pathViews.sports, _timestamp, batch)
      logBatch(batch, updates)
      await batch.commit()
    }

    if (onSuccess) onSuccess()

  } catch (err) {
    if (onError) onError(err)
    else console.error(err)
  }
}